/**
 * Per-Window Router — Route types.
 *
 * A WindowRoute is the canonical content descriptor for a window.
 * `window.entityId`, `window.title`, and `window.appType` are derived projections
 * of the current route, written by `reconcileWindowIdentity` in windowRouter.ts.
 * Apps read/write the route via `useWindowRoute(windowId)`; they never write
 * `window.entityId` directly.
 *
 * History (`routeHistory` / `routeIndex`) supersedes the legacy `navigationHistory`
 * (entityId string array) + `navigationIndex`. Both are kept in parallel during the
 * migration; the router writes both on every navigation.
 */

export interface WindowRouteParams {
  /** MongoDB _id of the primary entity displayed in this window. */
  entityId?: string;
  /** Entity llmId — redundant with entityId, kept for fast registry lookup. */
  entityLlmId?: string;
  /** How the app renders: full chrome (default), standalone (no list shell), focused. */
  mode?: 'full' | 'standalone' | 'focused';
  /** App-defined named view, e.g. 'inbox', 'overview', 'grid', 'drafts'. */
  view?: string;
  /**
   * AppInstance-backed windows have a permanent entityId that must not be
   * overwritten by setParams/replace. Set this flag when creating such a window.
   */
  pinnedIdentity?: boolean;
  /** Any additional app-defined navigational params. */
  [key: string]: unknown;
}

export interface WindowRoute {
  /** The appType string that renders this window (matches spaceStore/componentMap). */
  appType: string;
  params: WindowRouteParams;
}

/**
 * Derive a route from legacy window fields. Used on first-load backfill when
 * a window was persisted without a route (pre-router sessions).
 * Pure utility — no store dependencies, safe to import anywhere.
 */
export function deriveRouteFromWindow(window: any): WindowRoute {
  const mode = window.props?.renderMode as WindowRouteParams['mode'] | undefined;
  return {
    appType: window.appType || 'entity-view',
    params: {
      ...(window.entityId ? { entityId: window.entityId } : {}),
      ...(window.entityLlmId ? { entityLlmId: window.entityLlmId } : {}),
      ...(mode ? { mode } : {}),
    },
  };
}
